// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useCallback } from 'react';
import { logger } from './logger';
import { useChatStore } from '../store/chatStore';
import { ServerActionEnum } from '../types/game';
import type { ChatMessage } from '../types/chat';

interface WebSocketPayload {
  action: string;
  payload: Record<string, unknown>;
}

interface ChatMessagePayload extends ChatMessage {
  temp_message_id?: string;
}

export function useChatHandler() {
  const { addMessage, setMessages, updateTypingPlayers, updateReactions } = useChatStore();

  const handleChatMessage = useCallback(
    (message: WebSocketPayload) => {
      const { action, payload } = message;

      switch (action) {
        case ServerActionEnum.CHAT_NEW_MESSAGE: {
          logger.debug('chat_new_message_received', { payload });
          // Extract message from payload (backend sends {message: {...}})
          const { message } = payload as { message: ChatMessagePayload };
          const chatMessagePayload = message;

          // Check if this message has a temp_message_id for delivery confirmation
          if (chatMessagePayload.temp_message_id) {
            const tempId = chatMessagePayload.temp_message_id;
            const pendingIds = useChatStore.getState().pendingMessageIds;

            if (pendingIds.has(tempId)) {
              // This is our own message being confirmed
              logger.debug('chat_message_delivery_confirmed', {
                tempId,
                messageId: chatMessagePayload.message_id,
              });

              // Update the pending message with the real message data
              useChatStore.setState(state => {
                const newPendingIds = new Set(state.pendingMessageIds);
                newPendingIds.delete(tempId);
                return {
                  messages: state.messages.map(msg =>
                    msg.message_id === tempId
                      ? {
                          ...chatMessagePayload,
                          edited: chatMessagePayload.edited ?? false,
                          reactions: chatMessagePayload.reactions ?? {},
                          delivery_status: 'delivered' as const,
                        }
                      : msg
                  ),
                  pendingMessageIds: newPendingIds,
                };
              });
              break;
            }
          }

          // Fallback: Check if this is our own message by matching content and recent timestamp
          // This handles cases where backend doesn't echo temp_message_id
          const state = useChatStore.getState();

          logger.debug('checking_for_pending_match', {
            hasPending: state.pendingMessageIds.size > 0,
            pendingIds: Array.from(state.pendingMessageIds),
            newMessage: {
              player_id: chatMessagePayload.player_id,
              content: chatMessagePayload.content?.substring(0, 50),
              timestamp: chatMessagePayload.timestamp,
            },
          });

          const recentPendingMessage = state.messages.find(msg => {
            if (!state.pendingMessageIds.has(msg.message_id)) return false;

            // Match by player_id and content
            if (msg.player_id !== chatMessagePayload.player_id) {
              logger.debug('player_id_mismatch', {
                pending: msg.player_id,
                incoming: chatMessagePayload.player_id,
              });
              return false;
            }

            if (msg.content !== chatMessagePayload.content) {
              logger.debug('content_mismatch', {
                pending: msg.content?.substring(0, 50),
                incoming: chatMessagePayload.content?.substring(0, 50),
              });
              return false;
            }

            // Check if timestamps are within 30 seconds of each other
            const msgTime = new Date(msg.timestamp).getTime();
            const newMsgTime = new Date(chatMessagePayload.timestamp).getTime();
            const timeDiff = Math.abs(msgTime - newMsgTime);

            logger.debug('timestamp_check', {
              pendingTime: msg.timestamp,
              incomingTime: chatMessagePayload.timestamp,
              diffMs: timeDiff,
              withinTolerance: timeDiff < 30000,
            });

            return timeDiff < 30000; // 30 seconds tolerance
          });

          if (recentPendingMessage) {
            // This is our own message, replace the pending one
            logger.debug('chat_message_matched_by_content', {
              pendingId: recentPendingMessage.message_id,
              newId: chatMessagePayload.message_id,
            });

            useChatStore.setState(state => {
              const newPendingIds = new Set(state.pendingMessageIds);
              newPendingIds.delete(recentPendingMessage.message_id);
              return {
                messages: state.messages.map(msg =>
                  msg.message_id === recentPendingMessage.message_id
                    ? {
                        ...chatMessagePayload,
                        edited: chatMessagePayload.edited ?? false,
                        reactions: chatMessagePayload.reactions ?? {},
                        delivery_status: 'delivered' as const,
                      }
                    : msg
                ),
                pendingMessageIds: newPendingIds,
              };
            });
            break;
          }

          // Regular message from another player
          const chatMessage: ChatMessage = {
            message_id: chatMessagePayload.message_id,
            session_id: chatMessagePayload.session_id,
            player_id: chatMessagePayload.player_id,
            player_name: chatMessagePayload.player_name,
            content: chatMessagePayload.content,
            message_type: chatMessagePayload.message_type,
            timestamp: chatMessagePayload.timestamp,
            edited: chatMessagePayload.edited ?? false,
            reactions: chatMessagePayload.reactions ?? {},
          };
          addMessage(chatMessage);
          break;
        }

        case ServerActionEnum.CHAT_TYPING_UPDATE: {
          logger.debug('chat_typing_update_received', { payload });
          const typingPlayers = payload.typing_players as string[];
          updateTypingPlayers(typingPlayers);
          break;
        }

        case ServerActionEnum.CHAT_REACTION_ADDED:
        case ServerActionEnum.CHAT_REACTION_REMOVED: {
          logger.debug('chat_reaction_update_received', { payload });
          const messageId = payload.message_id as string;
          const reactions = payload.reactions as Record<string, string[]>;
          updateReactions({ message_id: messageId, reactions });
          break;
        }

        case ServerActionEnum.CHAT_HISTORY_RESPONSE: {
          logger.debug('chat_history_received', { payload });
          const messages = (payload.messages as ChatMessage[]).map(msg => ({
            ...msg,
            edited: msg.edited ?? false,
            reactions: msg.reactions ?? {},
          }));
          setMessages(messages);
          break;
        }

        default:
          // Not a chat message, ignore
          break;
      }
    },
    [addMessage, setMessages, updateTypingPlayers, updateReactions]
  );

  const isChatAction = useCallback((action: string): boolean => {
    return (
      action === ServerActionEnum.CHAT_NEW_MESSAGE ||
      action === ServerActionEnum.CHAT_TYPING_UPDATE ||
      action === ServerActionEnum.CHAT_REACTION_ADDED ||
      action === ServerActionEnum.CHAT_REACTION_REMOVED ||
      action === ServerActionEnum.CHAT_HISTORY_RESPONSE
    );
  }, []);

  return {
    handleChatMessage,
    isChatAction,
  };
}
